import React, { useState } from 'react';
import { Sparkles, Wrench, Bug, AlertTriangle, Calendar, Tag, Filter, Menu } from 'lucide-react';
import { MainSidebar } from '../components/layout/MainSidebar';

interface Change {
  type: 'feature' | 'improvement' | 'fix' | 'breaking';
  title: string;
  description?: string;
}

interface Release {
  version: string;
  date: string;
  status: 'upcoming' | 'current' | 'previous';
  changes: Change[];
}

const releases: Release[] = [
  {
    version: '1.4.0',
    date: 'Q1 2026',
    status: 'upcoming',
    changes: [
      {
        type: 'feature',
        title: 'Comment Analysis',
        description: 'Analyze and summarize comments filed in proceedings with AI-powered insights.',
      },
      {
        type: 'feature',
        title: 'Multi-docket Comparison',
        description: 'Compare multiple dockets side-by-side to identify patterns and differences.',
      },
      {
        type: 'feature',
        title: 'Advanced Export Options',
        description: 'Export analysis to PDF, Word, and integrate with citation management tools.',
      },
    ],
  },
  {
    version: '1.3.0',
    date: 'January 14, 2026',
    status: 'current',
    changes: [
      {
        type: 'feature',
        title: 'Temporal Analysis Across Collections',
        description: 'Track how policies and positions have evolved over time across multiple documents.',
      },
      {
        type: 'improvement',
        title: 'Response Citation Accuracy',
        description: 'Improved citation linking and source attribution in AI responses.',
      },
      {
        type: 'fix',
        title: 'Conversation Threading Issues',
        description: 'Fixed bugs where conversation threads were not properly maintained.',
      },
      {
        type: 'improvement',
        title: 'Usage Billing Transparency',
        description: 'Enhanced usage tracking dashboard with detailed breakdowns by feature.',
      },
    ],
  },
  {
    version: '1.2.0',
    date: 'January 6, 2026',
    status: 'previous',
    changes: [
      {
        type: 'feature',
        title: 'Workspace Collaboration',
        description: 'Share workspaces and analyses with team members.',
      },
      {
        type: 'improvement',
        title: 'Document Summarization Quality',
        description: 'Enhanced summarization with A++ grade accuracy and better key point extraction.',
      },
      {
        type: 'fix',
        title: 'OpenAI Rate Limiting',
        description: 'Improved handling of API rate limits with better retry logic.',
      },
    ],
  },
  {
    version: '1.1.0',
    date: 'December 2025',
    status: 'previous',
    changes: [
      {
        type: 'feature',
        title: 'Multi-Document Analysis Engine',
        description: 'Launched advanced multi-document analysis with visible reasoning steps.',
      },
      {
        type: 'improvement',
        title: 'Search Relevance with Cohere Reranking',
        description: 'Dramatically improved search accuracy using Cohere reranking models.',
      },
      {
        type: 'fix',
        title: 'Document Processing Pipeline',
        description: 'Stabilized document ingestion pipeline for more reliable processing.',
      },
    ],
  },
  {
    version: '1.0.0',
    date: 'November 2025',
    status: 'previous',
    changes: [
      {
        type: 'feature',
        title: 'Initial Release',
        description: 'CommsGenie launches with core FCC document analysis capabilities.',
      },
      {
        type: 'feature',
        title: 'FCC Document Analysis',
        description: 'AI-powered analysis of FCC dockets, orders, and filings.',
      },
      {
        type: 'feature',
        title: 'Chat Interface',
        description: 'Natural language chat interface for asking questions about documents.',
      },
      {
        type: 'feature',
        title: 'Stripe Billing Integration',
        description: 'Subscription and usage-based billing through Stripe.',
      },
    ],
  },
];

const getChangeIcon = (type: Change['type']) => {
  switch (type) {
    case 'feature':
      return <Sparkles className="w-4 h-4" />;
    case 'improvement':
      return <Wrench className="w-4 h-4" />;
    case 'fix':
      return <Bug className="w-4 h-4" />;
    case 'breaking':
      return <AlertTriangle className="w-4 h-4" />;
  }
};

const getChangeColor = (type: Change['type']) => {
  switch (type) {
    case 'feature':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'improvement':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'fix':
      return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'breaking':
      return 'text-red-600 bg-red-50 border-red-200';
  }
};

const getChangeLabel = (type: Change['type']) => {
  switch (type) {
    case 'feature':
      return 'NEW';
    case 'improvement':
      return 'IMPROVED';
    case 'fix':
      return 'FIX';
    case 'breaking':
      return 'BREAKING';
  }
};

const getStatusBadge = (status: Release['status']) => {
  switch (status) {
    case 'upcoming':
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-300">
          Upcoming
        </span>
      );
    case 'current':
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300">
          ✨ Current
        </span>
      );
    case 'previous':
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
          Previous
        </span>
      );
  }
};

interface ReleaseNotesPageProps {
  userEmail?: string | null;
  isAnonymous?: boolean;
  onSignIn?: () => void;
  onSignOut?: () => void;
}

export const ReleaseNotesPage: React.FC<ReleaseNotesPageProps> = ({
  userEmail = null,
  isAnonymous = false,
  onSignIn = () => {},
  onSignOut = () => {},
}) => {
  const [filterType, setFilterType] = useState<'all' | Change['type']>('all');
  const [isMainSidebarOpen, setIsMainSidebarOpen] = useState(false);

  const filteredReleases = releases.map((release) => ({
    ...release,
    changes:
      filterType === 'all'
        ? release.changes
        : release.changes.filter((change) => change.type === filterType),
  })).filter((release) => release.changes.length > 0);

  return (
    <div className="flex flex-col min-h-screen w-full bg-white">
      {/* Main App Sidebar - Overlay */}
      <MainSidebar
        isOpen={isMainSidebarOpen}
        onClose={() => setIsMainSidebarOpen(false)}
        alwaysOverlay={true}
        userEmail={userEmail}
        isAnonymous={isAnonymous}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200 w-full">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsMainSidebarOpen(!isMainSidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
              title="Toggle main menu"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            <Tag className="w-6 h-6 text-green-600" />
            <h1 className="text-xl font-bold text-gray-900">Release Notes</h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="w-full py-12 px-6">
        {/* Introduction */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            What's New in CommsGenie
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            Stay up to date with the latest features, improvements, and fixes. We're constantly 
            improving CommsGenie based on your feedback.
          </p>

          {/* Filter buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              All Changes
            </button>
            <button
              onClick={() => setFilterType('feature')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'feature'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Features
            </button>
            <button
              onClick={() => setFilterType('improvement')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'improvement'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Improvements
            </button>
            <button
              onClick={() => setFilterType('fix')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'fix'
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Fixes
            </button>
          </div>
        </div>

        {/* Roadmap callout for upcoming */}
        {filteredReleases.some((r) => r.status === 'upcoming') && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-purple-900 mb-2">
              🚀 Your Feedback Shapes Our Roadmap
            </h3>
            <p className="text-gray-700">
              The features below are planned for upcoming releases. Have suggestions or want to 
              prioritize a feature?{' '}
              <a href="/feedback" className="text-purple-700 font-medium hover:text-purple-800 underline">
                Send us feedback
              </a>
              .
            </p>
          </div>
        )}

        {/* Release timeline */}
        <div className="space-y-8">
          {filteredReleases.map((release, idx) => (
            <div
              key={release.version}
              className="relative bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
            >
              {/* Status indicator line on left */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${
                  release.status === 'upcoming'
                    ? 'bg-purple-500'
                    : release.status === 'current'
                    ? 'bg-green-500'
                    : 'bg-gray-300'
                }`}
              />

              <div className="p-6 pl-8">
                {/* Release header */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Version {release.version}
                    </h3>
                    {getStatusBadge(release.status)}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    {release.date}
                  </div>
                </div>

                {/* Changes list */}
                <div className="space-y-3">
                  {release.changes.map((change, changeIdx) => (
                    <div
                      key={changeIdx}
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div
                        className={`flex items-center justify-center w-8 h-8 rounded-lg border flex-shrink-0 ${getChangeColor(
                          change.type
                        )}`}
                      >
                        {getChangeIcon(change.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span
                            className={`text-xs font-bold px-2 py-0.5 rounded ${getChangeColor(
                              change.type
                            )}`}
                          >
                            {getChangeLabel(change.type)}
                          </span>
                          <h4 className="text-base font-semibold text-gray-900">
                            {change.title}
                          </h4>
                        </div>
                        {change.description && (
                          <p className="text-sm text-gray-600">{change.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Have Questions or Suggestions?
          </h3>
          <p className="text-gray-700 mb-6">
            We'd love to hear from you! Your feedback helps us build a better product.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/feedback"
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Send Feedback
            </a>
            <a
              href="/support"
              className="px-6 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Contact Support
            </a>
            <a
              href="/docs"
              className="px-6 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              View Documentation
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

